import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "./redux";
import { getCurrentAccountSelector, getUserCurrentAccountNameSelector, getUserIsDeveloperPartnerSelector, getUserIsServiceAccountSelector, getUserSelector, setUser } from "../reducers/userReducer";
import { useTRPC } from "../trpc";
import { useQueryClient } from "@tanstack/react-query";
import { clearAccountNameFromSession, setAccountNameToSession } from "@project/common";
import { identifyUser, resetSession } from "../utils/posthog";
import type { UserDetails } from "@project/api";

function setPosthogIdentity(user: UserDetails) {
  if (user.currentAccount) {
    identifyUser(user.id.toString(), {
      email: user.email,
      name: user.name,
      accountId: user.currentAccount.id,
      accountName: user.currentAccount.name,
      aboveFreeQuota: user.currentAccount.aboveFreeQuota,
    });
  }
  else {
    identifyUser(user.id.toString(), {
      email: user.email,
      name: user.name,
    });
  }
}

function setAccountSession(user: UserDetails) {
  if (user.currentAccount && user.persistent) {
    setAccountNameToSession(user.currentAccount.name);
  }
  else {
    clearAccountNameFromSession();
  }
}

/**
 * Set the user in the atom and update the current user in the trpc cache
 */
export const useSetUser = () => {
  const dispatch = useAppDispatch();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  return useCallback((user: UserDetails | null) => {
    if (!user) {
      resetSession();
      clearAccountNameFromSession();
      queryClient.clear();
      sessionStorage.clear();
    }
    else {
      setPosthogIdentity(user);
      setAccountSession(user);
      queryClient.setQueryData(
        trpc.getCurrentUser.queryKey(),
        user,
      );
    }
    dispatch(setUser(user));
  },
  [setUser, dispatch, queryClient],
  );
};

export const useUser = () => {
  return useAppSelector(getUserSelector);
};

export const useCurrentAccount = () => {
  return useAppSelector(getCurrentAccountSelector);
};

export const useSessionAccountName = () => {
  return useAppSelector(getUserCurrentAccountNameSelector);
};

export const useIsServiceAccount = () => {
  return useAppSelector(getUserIsServiceAccountSelector);
};

export const useIsDeveloperPartner = () => {
  return useAppSelector(getUserIsDeveloperPartnerSelector);
};

export function assertAccountSelected(user: UserDetails | null): asserts user is Required<UserDetails> {
  if (!user?.currentAccount) {
    throw new Error("No account selected");
  }
}

export const useNotIsAuthenticated = () => {
  const user = useUser();
  return !user?.currentAccount;
};

export const useIsAuthenticated = () => {
  const user = useUser();
  return user?.currentAccount;
};
